/**
 * @fileoverview Markdown to sanitised HTML, with `nostr:` links resolved to
 * local routes.
 *
 * TWO RULES SHAPE THIS FILE.
 *
 * 1. THE CONTENT IS SOMEONE ELSE'S. It arrives from a relay and a blob store,
 *    written by a stranger, and is rendered on our origin next to whatever else
 *    the reader has open here. Everything marked goes through DOMPurify before
 *    it is attached to a document, with no path around it.
 * 2. STORED LINKS ARE `nostr:` URIs, NOT OUR HOSTNAME. A page storing
 *    `https://pages.cloistr.xyz/naddr1...` has baked our host into portable
 *    content. The stored form is NIP-21 and the route is produced HERE, at
 *    display time, so the same bytes render correctly on a self-hosted
 *    instance, under a different domain, or in a different client entirely.
 *
 * WHY THE LINK WORK HAPPENS AFTER SANITISING AND ON THE DOM, not with a regex
 * over the markdown. A regex cannot tell a `nostr:` URI in prose from one
 * inside a fenced code block, and a docs page about Nostr is exactly the kind
 * of page that contains both. Walking the sanitised fragment and skipping
 * `<code>` and `<pre>` gets that right by construction.
 */

import DOMPurify from 'dompurify';
import { Marked, type Tokens } from 'marked';

import { directiveExtension } from './directives';
import { renderCodeBlock } from './highlight';

import { config } from '@/config/environment';
import { abbreviate } from '@/services/nostr/identifiers';
import { localRouteForNostrUri } from '@/services/nostr/pageLinks';
import { RENDERABLE_PAGE_KINDS } from '@/services/pages/kinds';

/**
 * DOMPurify's default URI allow-list has no `nostr:`, so it would strip the
 * hrefs this renderer exists to resolve. The default expression is reproduced
 * with `nostr` added and nothing else widened.
 */
const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|nostr):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

/**
 * `javascript:` and friends are handled by the allow-list above. This forbids
 * whole elements that are never legitimate output of a markdown renderer.
 *
 * `iframe` is on the list deliberately even though raw HTML pages will one day
 * be rendered INSIDE a sandboxed iframe: that iframe is ours, created by the
 * HTML branch, and an iframe arriving from page content is a different thing
 * wearing the same tag name.
 */
const FORBID_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'];

const NOSTR_URI = /\bnostr:(?:npub|note|nevent|nprofile|naddr)1[023456789acdefghjklmnpqrstuvwxyz]{20,}/gi;

export interface RenderedMarkdown {
  /** Ready to attach. Already sanitised; do not stringify and re-parse. */
  fragment: DocumentFragment;
  /** Headings in document order, for a table of contents. */
  headings: { id: string; text: string; level: number }[];
}

/**
 * What raw HTML typed into markdown becomes.
 *
 * `literal` for our own pages: page-format.md excludes raw HTML passthrough,
 * because a closed set means author input is only ever content inside our
 * templates, never markup. It is SHOWN as text rather than dropped, so the
 * author sees what they typed and that it did nothing.
 *
 * `sanitise` for a stranger's long-form article, which is not our format and
 * routinely carries HTML. Showing its tags as text would break pages we only
 * read, so it keeps the DOMPurify pass it has always had.
 */
export type RawHtml = 'literal' | 'sanitise';

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * One parser instance per raw-HTML rule, rather than the shared `marked`
 * singleton, so registering the directive vocabulary here cannot change what
 * any other caller of marked gets.
 */
function makeParser(rawHtml: RawHtml): Marked {
  return new Marked(directiveExtension(config.refUrls), {
    renderer: {
      code({ text, lang }) {
        return renderCodeBlock(text, lang);
      },
      ...(rawHtml === 'literal'
        ? {
            html(token: Tokens.HTML | Tokens.Tag) {
              return 'block' in token && token.block
                ? `<p>${escapeHtml(token.text.trim())}</p>\n`
                : escapeHtml(token.text);
            },
          }
        : {}),
    },
  });
}

const parsers: Record<RawHtml, Marked> = { literal: makeParser('literal'), sanitise: makeParser('sanitise') };

/**
 * Render markdown for display.
 *
 * Synchronous on purpose: `marked.parse` can return a promise when async
 * extensions are registered, and none are. Keeping it sync means a page cannot
 * half-render.
 */
export function renderMarkdown(
  markdown: string,
  { rawHtml = 'literal' }: { rawHtml?: RawHtml } = {}
): RenderedMarkdown {
  const rendered = parsers[rawHtml].parse(markdown, { async: false, gfm: true, breaks: false });

  // Sanitised in both modes: `literal` has escaped the author's HTML already,
  // and this pass still guards everything the directives and marked emit.
  const fragment = DOMPurify.sanitize(rendered, {
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_URI_REGEXP,
    FORBID_TAGS,
    FORBID_ATTR: ['style'],
  });

  resolveNostrLinks(fragment);
  linkifyBareNostrUris(fragment);
  const headings = anchorHeadings(fragment);
  fillInlineToc(fragment, headings);
  numberFootnotes(fragment);
  hardenExternalLinks(fragment);

  return { fragment, headings };
}

/**
 * Rewrite `<a href="nostr:...">` to a local route where we can, and strip the
 * href where we cannot.
 *
 * A reference we cannot render stays VISIBLE as text rather than being deleted:
 * the reader can still copy it into a client that understands it. Deleting it
 * would silently lose information the author put there, which is the worse of
 * the two failures.
 */
function resolveNostrLinks(root: ParentNode): void {
  for (const anchor of Array.from(root.querySelectorAll('a[href]'))) {
    const href = anchor.getAttribute('href') ?? '';
    if (!href.toLowerCase().startsWith('nostr:')) continue;

    const route = localRouteForNostrUri(href, RENDERABLE_PAGE_KINDS);
    if (route) {
      anchor.setAttribute('href', route);
      anchor.setAttribute('data-page-link', 'true');
    } else {
      anchor.removeAttribute('href');
      anchor.setAttribute('data-unresolved-nostr-uri', 'true');
    }
  }
}

/**
 * Turn bare `nostr:naddr1...` text into links.
 *
 * Markdown has no autolink rule for the `nostr:` scheme, so an author writing
 * the URI plainly -- which is what NIP-21 asks them to do -- would otherwise
 * get inert text. Text inside `<code>`, `<pre>` and existing `<a>` is skipped:
 * a URI in a code sample is being shown, not followed.
 */
function linkifyBareNostrUris(root: ParentNode): void {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (!text.data || !text.data.toLowerCase().includes('nostr:')) continue;
    if (text.parentElement?.closest('a, code, pre')) continue;
    targets.push(text);
  }

  for (const text of targets) {
    const parts = text.data.split(NOSTR_URI);
    const matches = text.data.match(NOSTR_URI) ?? [];
    if (matches.length === 0) continue;

    const replacement = doc.createDocumentFragment();
    parts.forEach((part, i) => {
      if (part) replacement.appendChild(doc.createTextNode(part));
      const uri = matches[i];
      if (!uri) return;
      const route = localRouteForNostrUri(uri, RENDERABLE_PAGE_KINDS);
      const label = abbreviate(uri.replace(/^nostr:/i, ''), 10);
      if (route) {
        const anchor = doc.createElement('a');
        anchor.setAttribute('href', route);
        anchor.setAttribute('data-page-link', 'true');
        anchor.textContent = label;
        replacement.appendChild(anchor);
      } else {
        const span = doc.createElement('span');
        span.setAttribute('data-unresolved-nostr-uri', 'true');
        span.textContent = uri;
        replacement.appendChild(span);
      }
    });
    text.replaceWith(replacement);
  }
}

/** Slug for a heading, stable enough to link to and unique within the page. */
function slugify(text: string, taken: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '') || 'section';
  let slug = base;
  let n = 2;
  while (taken.has(slug)) slug = `${base}-${n++}`;
  taken.add(slug);
  return slug;
}

function anchorHeadings(root: ParentNode): RenderedMarkdown['headings'] {
  const taken = new Set<string>();
  return Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6')).map((el) => {
    const text = el.textContent ?? '';
    const id = slugify(text, taken);
    el.setAttribute('id', id);
    return { id, text, level: Number(el.tagName.slice(1)) };
  });
}

/**
 * Fill each `:::toc` placeholder with the page's headings, down to its depth.
 * A page with nothing to list gets no box at all.
 */
function fillInlineToc(root: ParentNode, headings: RenderedMarkdown['headings']): void {
  for (const nav of Array.from(root.querySelectorAll('nav.pages-toc-inline'))) {
    const depth = Number(nav.getAttribute('data-depth')) || 2;
    const listed = headings.filter((h) => h.level >= 2 && h.level <= depth);
    if (listed.length === 0) {
      nav.remove();
      continue;
    }
    const doc = nav.ownerDocument;
    const list = doc.createElement('ul');
    for (const h of listed) {
      const item = doc.createElement('li');
      item.setAttribute('data-level', String(h.level));
      const link = doc.createElement('a');
      link.setAttribute('href', `#${h.id}`);
      link.textContent = h.text;
      item.appendChild(link);
      list.appendChild(item);
    }
    nav.appendChild(list);
  }
}

/**
 * Number footnote refs in reading order, and put the notes in that order.
 *
 * The author names a note; the reader sees a number. A ref with no note
 * becomes its name in brackets rather than a link to nowhere, and only the
 * first ref to a note carries the id its back link points at.
 */
function numberFootnotes(root: ParentNode): void {
  const notes = new Map<string, Element>();
  for (const li of Array.from(root.querySelectorAll('section.pages-footnotes li[data-fn]'))) {
    const id = li.getAttribute('data-fn')!;
    if (!notes.has(id)) notes.set(id, li);
  }

  const order: string[] = [];
  for (const sup of Array.from(root.querySelectorAll('sup.pages-fnref[data-fn]'))) {
    const id = sup.getAttribute('data-fn')!;
    if (!notes.has(id)) {
      sup.replaceWith(sup.ownerDocument.createTextNode(`[${id}]`));
      continue;
    }
    let n = order.indexOf(id) + 1;
    if (n === 0) {
      order.push(id);
      n = order.length;
      sup.querySelector('a')?.setAttribute('id', `pages-fnref-${id}`);
    }
    const link = sup.querySelector('a');
    if (link) link.textContent = String(n);
  }

  for (const id of order) {
    const li = notes.get(id)!;
    const back = li.ownerDocument.createElement('a');
    back.className = 'pages-fnback';
    back.setAttribute('href', `#pages-fnref-${id}`);
    back.setAttribute('aria-label', 'Back to text');
    back.textContent = '↩';
    li.append(' ', back);
    // Re-appending in reading order moves each note to its place; notes no
    // ref points at stay after them.
    li.parentElement?.appendChild(li);
  }
  for (const [id, li] of notes) {
    if (!order.includes(id)) li.parentElement?.appendChild(li);
  }
}

/**
 * Off-site links open in a new tab and cannot reach back through `window.opener`.
 *
 * Local routes are left alone so the router handles them without a reload.
 */
function hardenExternalLinks(root: ParentNode): void {
  for (const anchor of Array.from(root.querySelectorAll('a[href]'))) {
    const href = anchor.getAttribute('href') ?? '';
    if (href.startsWith('/') || href.startsWith('#')) continue;
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer nofollow');
  }
}
